import React, { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import imageCompression from "browser-image-compression";
import { showMessage } from "../Functionalities/Functions";
import "./ImageUploadWithCrop.css";

// Helper to create an Image from URL
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

// Canvas crop helper
async function getCroppedImg(imageSrc, pixelCrop, type = "image/jpeg") {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        resolve(null);
        return;
      }
      resolve(blob);
    }, type, 0.95);
  });
}

const EXTENSIONS = { "image/png": "png", "image/webp": "webp", "image/jpeg": "jpg" };

export const croppedFile = (blob, fileName, outputType = "image/jpeg") => {
  if (outputType === "image/jpeg") return new File([blob], fileName || "image.jpg", { type: outputType });
  const base = fileName ? fileName.replace(/\.[^.]+$/, "") : "image";
  return new File([blob], `${base}.${EXTENSIONS[outputType]}`, { type: outputType });
};

function ImageUploadWithCrop({
  label,
  value, // File object or URL string
  onChange,
  aspectRatio = 1,
  recommendedSizeText = "",
  compress = true,
  compressMaxRes = 182,
  required = false,
  disabled = false,
  lockShape = false,
  outputType = "image/jpeg",
}) {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [fileName, setFileName] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentAspectRatio, setCurrentAspectRatio] = useState(aspectRatio);

  useEffect(() => {
    setCurrentAspectRatio(aspectRatio);
  }, [aspectRatio]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFile = (file) => {
    if (!file) return;

    // Validate type
    const validTypes = [
      "image/png",
      "image/gif",
      "image/jpeg",
      "image/jpg",
      "image/webp",
      "image/svg+xml",
      "image/svg",
    ];
    if (!validTypes.includes(file.type)) {
      showMessage(
        "This format is not accepted",
        "danger",
        "Please add png/jpg/jpeg/gif/webp/svg files!",
        "top",
        "top-right",
        0
      );
      return;
    }

    // Validate size (1MB limit)
    if (file.size > 1024000) {
      showMessage(
        "Image file is too large",
        "danger",
        "Please add image less than 1 Mb!",
        "top",
        "top-right",
        0
      );
      return;
    }

    if (file.type === "image/gif") {
      onChange(file);
      return;
    }

    setFileName(file.name);
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      setImageSrc(reader.result);
      setModalOpen(true);
    });
    reader.readAsDataURL(file);
  };

  const onFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!disabled && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const performCrop = async () => {
    try {
      if (!imageSrc || !croppedAreaPixels) return;

      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, outputType);
      if (!croppedBlob) return;

      let finalFile = croppedFile(croppedBlob, fileName, outputType);

      // Compress if requested
      const fileExtension = fileName.split(".").pop().toLowerCase();
      if (compress && fileExtension !== "gif") {
        const options = {
          maxWidthOrHeight: compressMaxRes,
          useWebWorker: true,
          fileType: outputType,
        };
        try {
          const compressedFile = await imageCompression(finalFile, options);
          finalFile = new File([compressedFile], finalFile.name, {
            type: outputType,
          });
        } catch (error) {
          console.error("Compression failed, using cropped file: ", error);
        }
      }

      onChange(finalFile);
      closeModal();
    } catch (e) {
      console.error(e);
      showMessage(
        "Cropping failed",
        "danger",
        "Could not crop the image. Please try again.",
        "top",
        "top-right",
        0
      );
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const [objectUrl, setObjectUrl] = useState("");

  useEffect(() => {
    if (value instanceof File || value instanceof Blob) {
      const url = URL.createObjectURL(value);
      setObjectUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setObjectUrl("");
    }
  }, [value]);

  useEffect(() => {
    const handleGlobalPaste = (e) => {
      if (disabled || !isHovered) return;

      const items = e.clipboardData?.items;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf("image") !== -1) {
            const file = items[i].getAsFile();
            handleFile(file);
            e.preventDefault();
            break;
          }
        }
      }
    };

    window.addEventListener("paste", handleGlobalPaste);
    return () => {
      window.removeEventListener("paste", handleGlobalPaste);
    };
  }, [disabled, isHovered]);

  // Determine current label name / URL for display
  const getDisplayName = () => {
    if (value instanceof File || value instanceof Blob) {
      return value.name || "image.jpg";
    }
    if (Array.isArray(value) && value.length > 0) {
      const first = value[0];
      if (first && typeof first === "object" && first.image) {
        const parts = first.image.split("/");
        return parts[parts.length - 1] || "Current Image";
      }
      if (typeof first === "string") {
        const parts = first.split("/");
        return parts[parts.length - 1] || "Current Image";
      }
    }
    if (value && typeof value === "object") {
      if (value.name) return value.name;
      if (value.image) {
        const parts = value.image.split("/");
        return parts[parts.length - 1] || "Current Image";
      }
    }
    if (typeof value === "string" && value !== "") {
      const parts = value.split("/");
      return parts[parts.length - 1] || "Current Image";
    }
    return "Choose image";
  };

  const getPreviewUrl = () => {
    if (value instanceof File || value instanceof Blob) {
      return objectUrl;
    }
    if (Array.isArray(value) && value.length > 0) {
      const first = value[0];
      if (first && typeof first === "object" && first.image) {
        return first.image;
      }
      if (typeof first === "string") {
        return first;
      }
    }
    if (value && typeof value === "object") {
      if (value.image) return value.image;
    }
    if (typeof value === "string" && value !== "") {
      return value;
    }
    return null;
  };

  const previewSrc = getPreviewUrl();

  return (
    <div className={`img-upload-container ${disabled ? "disabled" : ""}`}>
      {label && (
        <label className="upload-label-title">
          {label} {recommendedSizeText && <span className="size-hint">({recommendedSizeText})</span>}
          {required && <span className="required-star">*</span>}
        </label>
      )}

      <div
        className={`upload-dropzone ${isDragOver ? "dragover" : ""} ${previewSrc ? "has-preview" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {previewSrc ? (
          <div className="preview-wrapper">
            <img src={previewSrc} alt="Preview" className="image-preview" />
            <div className="preview-overlay">
              <span className="overlay-text">Change Image</span>
            </div>
          </div>
        ) : (
          <div className="dropzone-placeholder">
            <i className="ti-image placeholder-icon"></i>
            <span className="placeholder-text">Drag & drop or Click to upload</span>
          </div>
        )}

        <input
          type="file"
          accept="image/*"
          className="hidden-file-input"
          onChange={onFileChange}
          disabled={disabled}
          onClick={(e) => {
            e.target.value = null; // Reset so onChange fires for same file
          }}
        />
      </div>

      {value && (
        <div className="file-info-bar">
          <span className="file-name" title={getDisplayName()}>
            {getDisplayName()}
          </span>
          <button
            type="button"
            className="clear-file-btn"
            onClick={() => onChange("")}
            disabled={disabled}
          >
            &times;
          </button>
        </div>
      )}

      {modalOpen && (
        <div className="crop-modal-overlay">
          <div className="crop-modal-card">
            <div className="crop-modal-header">
              <h5>Crop & Resize Image</h5>
              <button type="button" className="close-modal-btn" onClick={closeModal}>
                &times;
              </button>
            </div>

            <div className="crop-modal-body">
              <div className="cropper-container">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={currentAspectRatio}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              </div>
            </div>

            <div className="crop-modal-footer">
              <div className="zoom-controls">
                <i className="ti-minus zoom-icon"></i>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="zoom-slider"
                />
                <i className="ti-plus zoom-icon"></i>
              </div>

              {!lockShape && (
              <div className="crop-presets-container">
                <span className="presets-title">Recommended Sizes:</span>
                <div className="presets-list">
                  <button
                    type="button"
                    className={`preset-chip ${
                      Math.abs(currentAspectRatio - 1.667) < 0.01 ? "active" : ""
                    }`}
                    onClick={() => setCurrentAspectRatio(1.667)}
                  >
                    Hero Banner / Category Image (1.667)
                  </button>
                  <button
                    type="button"
                    className={`preset-chip ${
                      Math.abs(currentAspectRatio - 1) < 0.01 ? "active" : ""
                    }`}
                    onClick={() => setCurrentAspectRatio(1)}
                  >
                    Product / Square Banner (1:1)
                  </button>
                  <button
                    type="button"
                    className={`preset-chip ${
                      Math.abs(currentAspectRatio - 94 / 76) < 0.01 ? "active" : ""
                    }`}
                    onClick={() => setCurrentAspectRatio(94 / 76)}
                  >
                    Restaurant Image (94:76)
                  </button>
                  <button
                    type="button"
                    className={`preset-chip ${
                      Math.abs(currentAspectRatio - 240 / 130) < 0.01 ? "active" : ""
                    }`}
                    onClick={() => setCurrentAspectRatio(240 / 130)}
                  >
                    Rectangle Banner (240:130)
                  </button>
                </div>
              </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary mr-2" onClick={closeModal}>
                  Cancel
                </button>
                <button type="button" className="btn btn-success" onClick={performCrop}>
                  Crop & Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageUploadWithCrop;
